import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Tag, Space, Modal, Form, Select, DatePicker, Input, message, Progress, Drawer, Timeline, InputNumber, Divider } from 'antd';
import api from '../api/http';

// 干预状态与等级枚举（后端未强约束，前端统一）
const STATUS_OPTIONS = [
  { label:'待执行', value:'待执行', color:'default' },
  { label:'执行中', value:'执行中', color:'blue' },
  { label:'已完成', value:'已完成', color:'green' },
  { label:'暂停', value:'暂停', color:'red' }
];
const LEVEL_COLORS = { '一般':'blue', '重要':'orange', '紧急':'red' };

const BehaviorInterventionPage = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [tags, setTags] = useState([]);
  const [filters, setFilters] = useState({ status: undefined, employee_id: undefined });
  const [permissionMap, setPermissionMap] = useState({});

  // Create / Edit Modal
  const [createVisible, setCreateVisible] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  // Progress Modal
  const [progressVisible, setProgressVisible] = useState(false);
  const [progressForm] = Form.useForm();
  const [editingId, setEditingId] = useState(null);

  // Logs Drawer
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [logTarget, setLogTarget] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logForm] = Form.useForm();
  const [addingLog, setAddingLog] = useState(false);

  // 权限加载
  useEffect(()=>{
    try { const perms = JSON.parse(localStorage.getItem('permissions')||'[]'); const map={}; perms.forEach(p=>map[p]=true); setPermissionMap(map); } catch(e){}
  },[]);
  const canManage = permissionMap['behavior_intervention_manage'];

  const loadEmployees = useCallback(async ()=>{
    try {
      const { data } = await api.get('/employees', { params: { status: '在职' } });
      if(Array.isArray(data)) setEmployees(data);
    } catch(e){ /* ignore */ }
  },[]);
  const loadTags = useCallback(async ()=>{
    try {
      const { data } = await api.get('/behavior/tags', { params: { status: 'active' } });
      if(Array.isArray(data)) setTags(data);
    } catch(e){ /* ignore */ }
  },[]);

  const loadList = useCallback(async ()=>{
    setLoading(true);
    try {
      const params = {};
      if(filters.status) params.status = filters.status;
      if(filters.employee_id) params.employee_id = filters.employee_id;
      const { data } = await api.get('/behavior/interventions', { params });
      setList(data || []);
    } catch(e){
      message.error(e.response?.data?.error || e.message || '加载失败');
    }
    finally { setLoading(false); }
  },[filters]);

  useEffect(()=>{ loadEmployees(); loadTags(); loadList(); }, [loadEmployees, loadTags, loadList]);

  // 创建干预计划
  const submitCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      const payload = {
        employee_id: values.employee_id,
        primary_issue_tag_id: values.primary_issue_tag_id || null,
        level: values.level,
        start_date: values.start_date? values.start_date.format('YYYY-MM-DD') : null,
        target_date: values.target_date? values.target_date.format('YYYY-MM-DD') : null,
        expected_goal: values.expected_goal || null,
        plan_desc: values.plan_desc || null
      };
      await api.post('/behavior/interventions', payload);
      message.success('创建成功');
      setCreateVisible(false);
      form.resetFields();
      loadList();
    } catch(e){ if(!e.errorFields) message.error(e.message); }
    finally { setCreating(false); }
  };

  // 打开进度编辑
  const openProgress = (record) => {
    setEditingId(record.id);
    progressForm.setFieldsValue({
      progress_percent: record.progress_percent || 0,
      status: record.status || '待执行',
      plan_desc: record.plan_desc,
      expected_goal: record.expected_goal
    });
    setProgressVisible(true);
  };

  const submitProgress = async () => {
    try {
      const values = await progressForm.validateFields();
      const payload = { progress_percent: values.progress_percent, status: values.status, plan_desc: values.plan_desc, expected_goal: values.expected_goal };
      await api.put('/behavior/interventions/'+editingId, payload);
      message.success('更新成功');
      setProgressVisible(false);
      loadList();
    } catch(e){ if(!e.errorFields) message.error(e.message); }
  };

  // 日志
  const openLogs = async (record) => {
    setLogTarget(record);
    setLogDrawerOpen(true);
    await loadLogs(record.id);
  };
  const loadLogs = async (id) => {
    setLogLoading(true);
    try {
      const { data } = await api.get(`/behavior/interventions/${id}/logs`);
      setLogs(data || []);
    } catch(e){
      message.error(e.response?.data?.error || e.message || '日志加载失败');
    }
    finally { setLogLoading(false); }
  };
  const submitLog = async () => {
    try {
      const v = await logForm.validateFields();
      setAddingLog(true);
      await api.post(`/behavior/interventions/${logTarget.id}/logs`, v);
      message.success('日志已添加');
      logForm.resetFields();
      loadLogs(logTarget.id);
    } catch(e){ if(!e.errorFields) message.error(e.message); }
    finally { setAddingLog(false); }
  };

  const tagById = (id) => tags.find(t=>t.id===id);
  const empById = (id) => employees.find(e=>e.id===id);

  const columns = [
    { title:'ID', dataIndex:'id', width:70 },
    { title:'员工', dataIndex:'employee_id', render:v=> empById(v)?.name || v },
    { title:'主问题标签', dataIndex:'primary_issue_tag_id', render:v=> v? <Tag color='geekblue'>{tagById(v)?.name || v}</Tag> : '-' },
    { title:'等级', dataIndex:'level', render:v=> <Tag color={LEVEL_COLORS[v]||'default'}>{v}</Tag> },
    { title:'进度', dataIndex:'progress_percent', width:160, render:v=> <Progress percent={v||0} size='small' /> },
    { title:'状态', dataIndex:'status', render:v=> { const opt=STATUS_OPTIONS.find(o=>o.value===v); return <Tag color={opt?.color||'default'}>{opt?.label||v||'-'}</Tag>; } },
    { title:'起止', render:(_,r)=> (r.start_date||'-') + ' ~ ' + (r.target_date||'-') },
    { title:'操作', width:180, render:(_,r)=> <Space>
        <Button size='small' onClick={()=>openLogs(r)}>日志</Button>
        {canManage && <Button size='small' onClick={()=>openProgress(r)}>进度</Button>}
      </Space> }
  ];

  const filterBar = (
    <Space style={{ marginBottom:16 }} wrap>
      <Select
        allowClear
        placeholder='状态'
        style={{ width:140 }}
        value={filters.status}
        onChange={v=>{ setFilters(f=>({...f,status:v})); }}
        options={STATUS_OPTIONS.map(o=>({ label:o.label, value:o.value }))}
      />
      <Select
        allowClear
        showSearch
        placeholder='员工'
        style={{ width:200 }}
        value={filters.employee_id}
        onChange={v=>{ setFilters(f=>({...f,employee_id:v})); }}
        filterOption={(i,opt)=>(opt?.label??'').toLowerCase().includes(i.toLowerCase())}
        options={employees.map(e=>({ label:e.name+(e.department?' / '+e.department:''), value:e.id }))}
      />
      <Button onClick={()=>loadList()}>查询</Button>
      <Button onClick={()=>{ setFilters({}); }} disabled={!filters.status && !filters.employee_id}>重置</Button>
      {canManage && <Button type='primary' onClick={()=> setCreateVisible(true)}>新建计划</Button>}
    </Space>
  );

  return (
    <div style={{ padding:24 }}>
      <h2 style={{ marginBottom:8 }}>行为干预管理</h2>
      <p style={{ color:'#666', marginBottom:16 }}>管理并跟踪针对高风险员工的干预计划与执行日志。</p>
      {filterBar}
      <Table rowKey='id' size='small' loading={loading} columns={columns} dataSource={list} pagination={false} />

      {/* 创建计划 Modal */}
      <Modal
        title='新建干预计划'
        open={createVisible}
        onOk={submitCreate}
        onCancel={()=>{ setCreateVisible(false); form.resetFields(); }}
        confirmLoading={creating}
        destroyOnClose
        width={640}
      >
        <Form layout='vertical' form={form}>
          <Form.Item name='employee_id' label='员工' rules={[{ required:true, message:'请选择员工'}]}>
            <Select showSearch placeholder='员工' options={employees.map(e=>({ label:e.name+(e.department?' / '+e.department:''), value:e.id }))} filterOption={(i,opt)=>(opt?.label??'').toLowerCase().includes(i.toLowerCase())} />
          </Form.Item>
          <Form.Item name='primary_issue_tag_id' label='主问题标签'>
            <Select allowClear showSearch placeholder='选择标签' options={tags.map(t=>({ label:t.name, value:t.id }))} />
          </Form.Item>
          <Form.Item name='level' label='等级' initialValue='一般' rules={[{ required:true }]}>
            <Select options={[{label:'一般',value:'一般'},{label:'重要',value:'重要'},{label:'紧急',value:'紧急'}]} />
          </Form.Item>
          <Form.Item name='start_date' label='开始日期'>
            <DatePicker style={{ width:'100%' }} />
          </Form.Item>
            <Form.Item name='target_date' label='目标日期'>
              <DatePicker style={{ width:'100%' }} />
            </Form.Item>
          <Form.Item name='expected_goal' label='预期目标'>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name='plan_desc' label='计划说明'>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 进度/状态更新 Modal */}
      <Modal
        title='更新进度与状态'
        open={progressVisible}
        onOk={submitProgress}
        onCancel={()=>{ setProgressVisible(false); progressForm.resetFields(); }}
        destroyOnClose
      >
        <Form layout='vertical' form={progressForm}>
          <Form.Item name='progress_percent' label='进度(%)' rules={[{ required:true }]}>
            <InputNumber min={0} max={100} style={{ width:'100%' }} />
          </Form.Item>
          <Form.Item name='status' label='状态' rules={[{ required:true }]}>
            <Select options={STATUS_OPTIONS.map(o=>({ label:o.label, value:o.value }))} />
          </Form.Item>
          <Form.Item name='expected_goal' label='预期目标'>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name='plan_desc' label='计划说明'>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 日志 Drawer */}
      <Drawer
        title={`干预日志 #${logTarget?.id || ''}`}
        open={logDrawerOpen}
        width={560}
        onClose={()=>{ setLogDrawerOpen(false); setLogs([]); setLogTarget(null); }}
      >
        <Space direction='vertical' style={{ width:'100%' }} size='large'>
          <div style={{ maxHeight:300, overflow:'auto', paddingRight:8 }}>
            <Timeline
              pending={logLoading && '加载中...'}
              items={(logs||[]).map(l=>({
                children: (
                  <div>
                    <div style={{ fontWeight:'bold' }}>{l.action_desc || '无描述'}</div>
                    <div style={{ fontSize:12, color:'#888', margin:'4px 0' }}>{l.log_time}</div>
                    {l.result_metric && <div style={{ fontSize:12 }}>结果指标: {l.result_metric}</div>}
                    {l.next_step && <div style={{ fontSize:12 }}>下一步: {l.next_step}</div>}
                  </div>
                )
              }))}
            />
          </div>
          {canManage && (
            <div>
              <Divider orientation='left'>新增日志</Divider>
              <Form layout='vertical' form={logForm}>
                <Form.Item name='action_desc' label='行动描述' rules={[{ required:true, message:'请输入行动描述'}]}>
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item name='result_metric' label='结果指标'>
                  <Input />
                </Form.Item>
                <Form.Item name='next_step' label='下一步'>
                  <Input />
                </Form.Item>
                <Space>
                  <Button type='primary' loading={addingLog} onClick={submitLog}>提交</Button>
                  <Button onClick={()=>logForm.resetFields()}>重置</Button>
                </Space>
              </Form>
            </div>
          )}
        </Space>
      </Drawer>
    </div>
  );
};

export default BehaviorInterventionPage;
